// @ts-strict-ignore
import { Injectable } from "@angular/core";
import cloneDeep from "lodash-es/cloneDeep";
import { ExpertModeProfileDto } from "src/app/shared/models/expert-profile.model";
import { ExpertModeElement } from "../../components/expert/expert.component";
import { ProfileElement, ProfileElementSchema, ProfileElementScreenInput } from "../models/profile-element.model";

@Injectable({ providedIn: "root" })
export class ExpertScreensGeneratorService {
  generateExpertModeScreens(editedProfile: ExpertModeProfileDto, activeSchema: ProfileElementSchema): ExpertModeElement[] {
    //this.logService.log("generateExpertModeScreens");
    //this.logService.log("editedProfile", editedProfile);
    //this.logService.log("activeSchema", activeSchema);

    const newExpertModeScreens = [];
    // Construct a new object, for every PE defined by editedProfile seed the PE/screens/inputs based on the PE definition then for each value of the PE defined by editedProfile, set the input's value to that
    for (const profileElement of editedProfile.elements) {
      let newProfileElement = this.getProfileElementDefinition(profileElement.key, activeSchema);

      // If we cannot find the element by definition, the saved profile contains data for an element no longer in the loaded schema
      if (newProfileElement) {
        newProfileElement = {
          ...cloneDeep({ ...newProfileElement, instanceId: profileElement.instanceId })
        };

        for (const screen of newProfileElement.screens) {
          screen.inputs = cloneDeep(this.generateExpertModeScreensNested(screen.inputs, profileElement, editedProfile, activeSchema));
        }

        newExpertModeScreens.push(newProfileElement);
      } /*else {
        console.warn(
          "Warning: Dropped an element from the loaded profile as it doesn't exist in the active schema.",
          profileElement
        );
      }*/
    }

    // Inject any mandatory elements from the active schema that are not already in the loaded profile, this could occur if the profile was generated from another schema and what is active
    for (const activeSchemaElement of activeSchema.elements) {
      if (activeSchemaElement.minInstances > 0) {
        let elementFoundCount = 0;

        for (const loadedProfileElement of newExpertModeScreens) {
          if (activeSchemaElement.key === loadedProfileElement.key) {
            elementFoundCount++;
          }
        }

        if (elementFoundCount < activeSchemaElement.minInstances) {
          for (let i = elementFoundCount; i < activeSchemaElement.minInstances; i++) {
            const newProfileElement = this.getProfileElementDefinition(activeSchemaElement.key, activeSchema);

            if (newProfileElement) {
              newExpertModeScreens.push(cloneDeep(newProfileElement));

              /*console.warn(
                "Warning: Adding a mandatory element instance for active schema that was not present in the profile being loaded",
                newProfileElement
              );*/
            }
          }
        }
      }
    }

    // Find any inputs from editedProfile that haven't been added yet and see if there are matches for them in the activeSchema, if so they may need to be sent to elements already added or a new element may need to be added to house them
    if (editedProfile.schemaKey !== activeSchema.key) {
      //console.warn("triggering deep check of input import");

      for (const element of editedProfile.elements) {
        if (element.values) {
          for (const value of element.values) {
            let inputAlreadyImported = false;
            let inputExistsInNewSchema = false;

            for (const newElement of newExpertModeScreens) {
              if (newElement.screens) {
                for (const newScreen of newElement.screens) {
                  if (newScreen.inputs) {
                    for (const newInput of newScreen.inputs) {
                      if (value.instanceId === newInput.instanceId) {
                        //console.warn("input already exits in new profile", value);

                        inputAlreadyImported = true;
                        break;
                      }
                    }
                  }

                  if (inputAlreadyImported) {
                    break;
                  }
                }
              }

              if (inputAlreadyImported) {
                break;
              }
            }

            for (const schemaElement of activeSchema.elements) {
              if (schemaElement.screens) {
                for (const schemaScreen of schemaElement.screens) {
                  if (schemaScreen.inputs) {
                    for (const screenInput of schemaScreen.inputs) {
                      if (value.key === screenInput.key) {
                        //console.warn("input key is found in new schema");

                        inputExistsInNewSchema = true;

                        if (!inputAlreadyImported && inputExistsInNewSchema) {
                          //console.warn("input requires housing", value);

                          let elementExists = false;
                          let inputInjected = false;

                          // See if this element already exists in the new profile, if so just place the input's value there, otherwise add the element
                          for (const newElement of newExpertModeScreens) {
                            if (newElement.key === schemaElement.key) {
                              //console.warn("the element required to house the input already exists", newElement);

                              elementExists = true;

                              break;
                            }
                          }

                          if (!elementExists) {
                            //console.warn("the element required to house the input does not exist yet");

                            const newProfileElement = this.getProfileElementDefinition(schemaElement.key, activeSchema);

                            if (newProfileElement) {
                              //console.warn("added required element to house input", newProfileElement);

                              newExpertModeScreens.push(cloneDeep(newProfileElement));

                              if (newProfileElement.requires) {
                                /*console.warn(
                                  "added element requires another element to be present",
                                  newExpertModeScreens,
                                  newProfileElement
                                );*/

                                this.generateExpertModeScreensInsertElementDependencies(
                                  newExpertModeScreens,
                                  newProfileElement,
                                  activeSchema
                                );
                              }
                            }
                          }

                          for (const newElement of newExpertModeScreens) {
                            if (newElement.key === schemaElement.key) {
                              if (newElement.screens) {
                                for (const newScreen of newElement.screens) {
                                  if (newScreen.inputs) {
                                    for (const newInput of newScreen.inputs) {
                                      if (newInput.key === value.key) {
                                        // Array inputs require special handling to match records and their values with input definitions
                                        if (newInput.type === "array" || newInput.type === "object") {
                                          const arrayRecords = [];

                                          if (value.value) {
                                            for (const valueRecord of value.value) {
                                              const arrayRecord = [];

                                              for (const inputDefinition of newInput.inputs) {
                                                let inputKeyMatched = false;

                                                for (const valueRecordValue of valueRecord) {
                                                  // If we find a match for the key, use the instance/value from the imported data
                                                  if (valueRecordValue.key === inputDefinition.key) {
                                                    arrayRecord.push({
                                                      ...cloneDeep(inputDefinition),
                                                      instanceId: valueRecordValue.instanceId,
                                                      value: valueRecordValue.value ?? null
                                                    });

                                                    inputKeyMatched = true;

                                                    break;
                                                  }
                                                }

                                                // If there was no key matches from the imported data then make a new instance of the array input as it may not have existed in the old schema
                                                if (!inputKeyMatched) {
                                                  arrayRecord.push({
                                                    ...cloneDeep(inputDefinition),
                                                    instanceId: crypto.randomUUID(),
                                                    value: inputDefinition.value ?? null
                                                  });
                                                }
                                              }

                                              arrayRecords.push(arrayRecord);
                                            }
                                          }

                                          newInput.instanceId = value.instanceId;
                                          newInput.value = cloneDeep(arrayRecords);
                                        } else {
                                          newInput.instanceId = value.instanceId;
                                          newInput.value = cloneDeep(value.value);
                                        }

                                        inputInjected = true;
                                        break;
                                      }
                                    }
                                  }

                                  if (inputInjected) {
                                    break;
                                  }
                                }
                              }
                            }
                          }
                        }

                        break;
                      }
                    }
                  }

                  if (inputExistsInNewSchema) {
                    break;
                  }
                }
              }

              if (inputExistsInNewSchema) {
                break;
              }
            }
          }
        }
      }
    }

    // Process that ensures all elements/inputs have a generated instanceId, and a seeded null value, mainly for the injection of mandatory elements from switching schemas since that simply spreads the ProfileElement definition
    if (newExpertModeScreens) {
      for (const element of newExpertModeScreens) {
        if (!element.instanceId) {
          element.instanceId = crypto.randomUUID();
        }

        if (element.screens) {
          for (const screen of element.screens) {
            if (screen.inputs) {
              for (const input of screen.inputs) {
                if (!input.instanceId) {
                  input.instanceId = crypto.randomUUID();
                }

                // For any inputs (ignoring 'displaytext' types) ensure there is a value property and seed it to null
                if (input.type !== "displaytext") {
                  if (!input.value) {
                    //console.warn("null coalescing falsy value on input to itself or null", input);

                    input.value = input.value ?? null;
                  }
                }
              }
            }
          }
        }
      }
    }

    //this.logService.log("expertModeScreens", newExpertModeScreens);

    return newExpertModeScreens;
  }

  generateExpertModeScreensNested(
    inputs: ProfileElementScreenInput[],
    profileElement: any,
    editedProfile: ExpertModeProfileDto,
    activeSchema: ProfileElementSchema
  ) {
    //this.logService.log("generateExpertModeScreensNested", inputs, profileElement);

    const newInputs = [];
    if (inputs) {
      for (const input of inputs) {
        let inputFound = false;

        if (profileElement.values) {
          for (const profileElementInput of profileElement.values) {
            if (input.key === profileElementInput.key) {
              //console.info("input found", input);

              inputFound = true;

              // Array inputs require special handling to match records and their values with input definitions
              if (input.type === "array" || input.type === "object") {
                const arrayRecords = [];

                if (profileElementInput.value) {
                  for (const record of profileElementInput.value) {
                    //this.logService.log("checking record", record);

                    const arrayRecord = [];

                    for (const inputDefinition of input.inputs) {
                      let inputDefinitionFound = false;

                      for (const value of record) {
                        //this.logService.log("checking value of record", value, record);

                        if (value.key === inputDefinition.key) {
                          inputDefinitionFound = true;
                          if (inputDefinition.type === "array" || inputDefinition.type === "object") {
                            //console.warn("found nested array|object to load", input, inputDefinition, value);

                            const nestedInputsArray = [];

                            // eslint-disable-next-line @typescript-eslint/prefer-for-of
                            for (let i = 0; i < value?.value?.length; i++) {
                              const nestedInputs = cloneDeep(
                                this.generateExpertModeScreensNested(
                                  inputDefinition.inputs,
                                  { values: value.value[i] },
                                  editedProfile,
                                  activeSchema
                                )
                              );

                              nestedInputsArray.push(nestedInputs);
                            }

                            //console.warn({ ...cloneDeep(inputDefinition), instanceId: value.instanceId, value: nestedInputsArray });

                            arrayRecord.push({
                              ...cloneDeep(inputDefinition),
                              instanceId: value.instanceId,
                              value: nestedInputsArray
                            });
                          } else {
                            arrayRecord.push({
                              ...cloneDeep(inputDefinition),
                              instanceId: value.instanceId,
                              value: value.value ?? null
                            });
                          }

                          break;
                        }
                      }

                      if (!inputDefinitionFound) {
                        //console.warn("could not find input in source, generate a new instance", inputDefinition);

                        arrayRecord.push(
                          cloneDeep({
                            ...inputDefinition,
                            instanceId: crypto.randomUUID(),
                            value: inputDefinition.value ?? null
                          })
                        );
                      } else {
                        //console.info("found input in source, attaching as is", arrayRecord);
                      }
                    }

                    arrayRecords.push(arrayRecord);
                  }
                }

                newInputs.push(
                  cloneDeep({
                    ...input,
                    instanceId: profileElementInput.instanceId,
                    value: arrayRecords
                  })
                );
              } else {
                let inputValue = profileElementInput.value;

                // For options based inputs, check if its value exists as an option, if not reset it to null
                if (input.type === "radio" || input.type === "dropdown") {
                  if (profileElementInput.value && input.options) {
                    let optionValueFound = false;

                    for (const option of input.options) {
                      if (option.value === profileElementInput.value) {
                        optionValueFound = true;
                        break;
                      }
                    }

                    if (!optionValueFound) {
                      inputValue = null;
                    }
                  }
                }

                newInputs.push(
                  cloneDeep({
                    ...input,
                    instanceId: profileElementInput.instanceId,
                    value: inputValue
                  })
                );
              }

              break;
            }
          }
        }

        // If the screen's input was not found in the original data then add a new instance of it, this can happen if the field was added after the data was saved
        if (!inputFound) {
          //console.error("input not found", input);

          newInputs.push(
            cloneDeep({
              ...input,
              instanceId: crypto.randomUUID(),
              value: input.value ?? null
            })
          );
        }
      }
    }

    return newInputs;
  }

  generateExpertModeScreensInsertElementDependencies(
    screens: ProfileElement[],
    element: ProfileElement,
    activeSchema: ProfileElementSchema
  ) {
    const newProfileElement = this.getProfileElementDefinition(element.requires, activeSchema);

    if (newProfileElement) {
      screens.push(cloneDeep(newProfileElement));

      if (newProfileElement.requires) {
        let foundElement = false;

        for (const screen of screens) {
          if (screen.key === newProfileElement.requires) {
            foundElement = true;
            break;
          }
        }

        if (!foundElement) {
          this.generateExpertModeScreensInsertElementDependencies(screens, newProfileElement, activeSchema);
        }
      }
    }
  }

  getProfileElementDefinition(key: string, activeSchema: ProfileElementSchema): ProfileElement | null {
    for (const element of activeSchema.elements) {
      if (element.key === key) {
        return element;
      }
    }

    return null;
  }
}
